import { PARTS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useCallback, useEffect, useState } from "react";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Chip, Divider, LinearProgress, Typography } from "@mui/material";
import { Inventory2, Sell, Savings } from "@mui/icons-material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDate, formatDateTime, formatMoney, formatQuantity } from "../../../../utils";
import {
  DetailRow,
  DetailSection,
  DetailValue,
  InfoHint,
} from "../../../../components/ui/detailRow/detailRow.component";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { buildOrderedPartReferenceId } from "../../../../utils/entityRoutes";
import { StatCard } from "../../../dashboard/dashboard.component";

/** One still-usable batch, as the Part detail endpoint reports it. */
type BatchPrice = {
  ordered_part_id: number;
  number: string | null;
  delivered_at: string | null;
  quantity: string;
  availability: string;
  price_net: string;
  vat: string;
  price_gross: string;
};

type StockSummary = {
  ordered_quantity: string;
  used_quantity: string;
  available_quantity: string;
  last_delivered_at: string | null;
  stock_value_net: string;
  stock_value_gross: string;
};

type Part = {
  id: number;
  name: string;
  code: string | null;
  unit: string;
  stock_quantity: string;
  notification_threshold: string | null;
  is_below_notification_threshold: boolean;
  current_price: BatchPrice | null;
  next_issue: BatchPrice | null;
  stock_summary: StockSummary;
  created_at: string;
  updated_at: string;
};

/** One batch's price, as a stacked block: the net price, its gross underneath,
 * then the order it comes from on its own line.
 *
 * The order link is deliberately not nested inside a caption Typography -
 * EntityReferenceLink sets its own 0.875rem size, so next to 0.75rem caption
 * text it renders visibly out of scale. Giving it its own line at body2 size
 * keeps the two matched. */
const BatchPriceBlock = ({
  heading,
  priceNet,
  priceGross,
  link,
  footnote,
}: {
  heading?: string;
  priceNet: string;
  priceGross: string;
  link: ReactNode;
  footnote?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
      {heading && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {heading}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {formatMoney(priceNet)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatMoney(priceGross)} {t("Gross")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap",
          fontSize: "0.875rem",
          color: "text.secondary",
        }}
      >
        {link}
        {footnote && <Box component="span">{footnote}</Box>}
      </Box>
    </Box>
  );
};

export const PartBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(PARTS_ENDPOINT);
  const [part, setPart] = useState<Part | null>(null);
  const canViewParts = useHasPermission("service.view_part");
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");

  useEffect(() => {
    const fetchPart = async () => {
      if (id) {
        const response = await get(id);
        setPart(response.data);
      }
    };

    const handlePartUpdated = () => {
      fetchPart();
    };

    // The header's stock, price and value all move when a batch is ordered or
    // used, so refresh on those events too - not just on edits to the part.
    const events = ["partUpdated", "orderedPartAdded", "orderedPartUpdated", "usedPartAdded", "usedPartUpdated"];
    events.forEach((event) => window.addEventListener(event, handlePartUpdated));
    fetchPart();
    return () => {
      events.forEach((event) => window.removeEventListener(event, handlePartUpdated));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const partId = part?.id;
  const renderBatchLink = useCallback(
    (batch: BatchPrice) => (
      <EntityReferenceLink
        entityType="orderedPart"
        entityId={
          partId === undefined
            ? undefined
            : buildOrderedPartReferenceId(partId, batch.ordered_part_id)
        }
        label={batch.number ?? undefined}
        canPreview={canViewOrderedParts}
      />
    ),
    [canViewOrderedParts, partId]
  );

  if (!canViewParts) {
    return null;
  }

  if (!part) return <CustomCircularProgress />;

  const summary = part.stock_summary;
  const threshold = part.notification_threshold;
  const isLow = part.is_below_notification_threshold;
  const stockValue = Number(part.stock_quantity);
  const thresholdValue = threshold === null ? null : Number(threshold);
  // How close the shelf is to the alarm. Pinned at full once stock is clear of
  // the threshold - past that the exact ratio says nothing useful.
  // The two prices only differ once a part has more than one open batch. With
  // a single one - the common case - they are the same order, so labelling
  // them separately would just repeat the same figures twice.
  const showsTwoBatches =
    part.next_issue !== null &&
    part.current_price !== null &&
    part.next_issue.ordered_part_id !== part.current_price.ordered_part_id;
  const thresholdProgress =
    thresholdValue && thresholdValue > 0
      ? Math.max(0, Math.min(100, (stockValue / thresholdValue) * 100))
      : null;

  return (
    <Box sx={basicInfoContainerStyle}>
      {/* Header */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            {part.name}
          </Typography>
          {part.code && <Chip size="small" variant="outlined" label={part.code} />}
          {isLow && (
            <Chip size="small" color="error" label={t("Below notification threshold")} />
          )}
        </Box>
      </Box>

      {/* The three numbers the client asked to see without clicking further. */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
        <StatCard
          title={t("Stock Quantity")}
          value={(
            <Box sx={{ minWidth: 170 }}>
              <Typography
                variant="h4"
                component="div"
                fontWeight="bold"
                color={isLow ? "error.main" : undefined}
              >
                {`${formatQuantity(part.stock_quantity)} ${part.unit}`}
              </Typography>
              {thresholdProgress !== null ? (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={thresholdProgress}
                    color={isLow ? "error" : "success"}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {`${t("Notification Threshold")}: ${formatQuantity(threshold)}`}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t("No notification threshold set")}
                </Typography>
              )}
            </Box>
          )}
          icon={<Inventory2 />}
          color={isLow ? "#d32f2f" : "#2e7d32"}
        />
        <StatCard
          title={t("Current Price")}
          value={(
            <Box sx={{ minWidth: 170 }}>
              {part.current_price ? (
                <>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    {formatMoney(part.current_price.price_net)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {`${formatMoney(part.current_price.price_gross)} ${t("Gross")} (+${formatQuantity(part.current_price.vat)}% ${t("VAT")})`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {`${t("From Order")} ${part.current_price.number ?? "-"}`}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("No delivered order with stock left")}
                </Typography>
              )}
            </Box>
          )}
          icon={<Sell />}
          color="#0288d1"
        />
        <StatCard
          title={t("Stock Value")}
          value={(
            <Box sx={{ minWidth: 170 }}>
              <Typography variant="h4" component="div" fontWeight="bold">
                {formatMoney(summary.stock_value_net)}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {`${formatMoney(summary.stock_value_gross)} ${t("Gross")}`}
              </Typography>
            </Box>
          )}
          icon={<Savings />}
          color="#f57c00"
        />
      </Box>

      <DetailSection title={t("Part Details")}>
        <DetailRow label={t("Code")} value={<DetailValue>{part.code || "-"}</DetailValue>} />
        <DetailRow label={t("Name")} value={<DetailValue>{part.name || "-"}</DetailValue>} />
        <DetailRow label={t("Unit")} value={<DetailValue>{part.unit || "-"}</DetailValue>} />
        <DetailRow
          label={t("Stock Quantity")}
          value={(
            <DetailValue>
              {`${formatQuantity(part.stock_quantity)} ${part.unit}`}
            </DetailValue>
          )}
        />
        <DetailRow
          label={(
            <>
              {t("Notification Threshold")}
              <InfoHint text={t("A notification is sent once stock reaches this level.")} />
            </>
          )}
          value={(
            <DetailValue>
              {threshold === null ? "-" : `${formatQuantity(threshold)} ${part.unit}`}
            </DetailValue>
          )}
        />
        <DetailRow
          label={t("Total Ordered")}
          value={(
            <DetailValue>
              {`${formatQuantity(summary.ordered_quantity)} ${part.unit}`}
            </DetailValue>
          )}
        />
        <DetailRow
          label={t("Total Used")}
          value={(
            <DetailValue>
              {`${formatQuantity(summary.used_quantity)} ${part.unit}`}
            </DetailValue>
          )}
        />
        <DetailRow
          label={t("Last Delivery")}
          value={(
            <DetailValue>
              {summary.last_delivered_at ? formatDate(summary.last_delivered_at) : "-"}
            </DetailValue>
          )}
        />
        <DetailRow
          label={(
            <>
              {t("Current Price")}
              <InfoHint
                text={t(
                  "Stock is used oldest delivery first, so the next use is charged against the oldest order still holding stock - a different price from the newest one whenever the part was re-ordered at a new rate."
                )}
              />
            </>
          )}
          noBorder
          alignTop
          value={
            part.current_price ? (
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", minWidth: 0 }}>
                <BatchPriceBlock
                  // Only worth naming which batch is which when they differ;
                  // with one open order the two are the same row of data.
                  heading={showsTwoBatches ? t("Newest order") : undefined}
                  priceNet={part.current_price.price_net}
                  priceGross={part.current_price.price_gross}
                  link={renderBatchLink(part.current_price)}
                  footnote={`· ${formatQuantity(part.current_price.availability)} ${part.unit} ${t("left")}`}
                />
                {showsTwoBatches && part.next_issue && (
                  <BatchPriceBlock
                    heading={t("Next issued from")}
                    priceNet={part.next_issue.price_net}
                    priceGross={part.next_issue.price_gross}
                    link={renderBatchLink(part.next_issue)}
                    footnote={`· ${formatQuantity(part.next_issue.availability)} ${part.unit} ${t("left")}`}
                  />
                )}
              </Box>
            ) : (
              <DetailValue>-</DetailValue>
            )
          }
        />
      </DetailSection>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{t("Created At")}:</strong> {formatDateTime(part.created_at)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{t("Updated At")}:</strong> {formatDateTime(part.updated_at)}
        </Typography>
      </Box>
    </Box>
  );
};
